import React, { useState, useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiShare } from 'react-icons/fi';
import { FaSafari } from 'react-icons/fa';
import { AiOutlinePlusSquare } from 'react-icons/ai';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { MdAddToHomeScreen } from 'react-icons/md';
import { useModal } from 'store/modal';
import Button from 'components/Button';

interface AddToHomeScreenComponentProps {
  icon?: ReactNode;
  label: string;
  type?: string;
  setIsMenuVisible?: (value: boolean) => void;
}

const AddToHomeScreenComponent: React.FC<AddToHomeScreenComponentProps> = ({ icon, label, type, setIsMenuVisible }) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [deferredPrompt, setDeferredPrompt] = useState<any | null>(null);
  const [isCompatible, setIsCompatible] = useState<boolean>(false);
  const [isAppInstalled, setIsAppInstalled] = useState<boolean>(false);
  const [isIosChrome, setIsIosChrome] = useState<boolean>(false);

  useEffect(() => {
    if ('getInstalledRelatedApps' in navigator) {
      navigator.getInstalledRelatedApps().then(relatedApps => {
        if (relatedApps.length > 0) {
          setIsAppInstalled(true);
        }
      });
    }
    if (window.navigator.standalone) {
      setIsAppInstalled(true);
    }
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setIsCompatible(true);
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleClick = () => {
    if (deferredPrompt) {
      // Directly prompt the user if deferredPrompt is available
      (deferredPrompt as any).prompt();
      (deferredPrompt as any).userChoice.then((choiceResult: any) => {
        if (setIsMenuVisible) {
          setIsMenuVisible(false);
        }
        setDeferredPrompt(null);
      });
    } else if (isChromeOnIOS()) {
      // Show IOS instuctions for chrome
      handleChromeOnIosInstructions();
    } else if (isMobileSafari()) {
      // Show iOS instructions
      handleIosInstructions();
    } else {
      // Show browser not supported message
      handleBrowserNotSupported();
    }
  };

  const isMobileSafari = (): boolean => {
    return /iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(navigator.userAgent);
  };

  const isChromeOnIOS = (): boolean => {
    return /CriOS/i.test(navigator.userAgent);
  };

  const handleBrowserNotSupported = () => {
    showModal({
      type: 'info',
      heading: t('addToHomescreen.title'),
      size: 'lg',
      content: (
        <div className="d-flex flex-column text-left px-3">
          <hr className="w-100 mt-0 mb-2" />
          <p className="text-center mb-2">{t('addToHomescreen.description')}</p>
          <p className="text-center mb-2 mt-2">{t('addToHomescreen.description2')}</p>
          <hr className="w-100 mt-0 mb-4" />
          <p>
            <BsThreeDotsVertical
              size={25}
              style={{ marginRight: '10px' }}
            />{' '}
            {t('addToHomescreen.androidInstallInscructions1')}
          </p>
          <p>
            <MdAddToHomeScreen
              size={25}
              style={{ marginRight: '10px' }}
            />{' '}
            {t('addToHomescreen.androidInstallInscructions2')}
          </p>
        </div>
      ),
    });
  };

  const handleChromeOnIosInstructions = () => {
    showModal({
      type: 'info',
      heading: t('addToHomescreen.title'),
      size: 'lg',
      content: (
        <div className="d-flex flex-column text-left px-3">
          <hr className="w-100 mt-0 mb-2" />
          <p className="text-center mb-2">{t('addToHomescreen.description')}</p>
          <hr className="w-100 mt-0 mb-4" />
          <div className="d-flex">
            <FiShare
              size={25}
              style={{ marginRight: '10px' }}
            />{' '}
            <p>{t('addToHomescreen.iosInstallInstructionsChrome1')}</p>
          </div>
          <div className="d-flex">
            <p>
              <AiOutlinePlusSquare
                size={25}
                style={{ marginRight: '10px' }}
              />{' '}
              {t('addToHomescreen.iosInstallInstructions2')}
            </p>
          </div>
          <div className="d-flex">
            <p>
              <MdAddToHomeScreen
                size={25}
                style={{ marginRight: '10px' }}
              />{' '}
              {t('addToHomescreen.iosInstallInstructions3')}
            </p>
          </div>
        </div>
      ),
    });
  };

  const handleIosInstructions = () => {
    showModal({
      type: 'info',
      heading: t('addToHomescreen.title'),
      size: 'lg',
      content: (
        <div className="d-flex flex-column text-left px-3">
          <hr className="w-100 mt-0 mb-2" />
          <p className="text-center mb-2">{t('addToHomescreen.description')}</p>
          <hr className="w-100 mt-0 mb-4" />
          <div className="d-flex">
            <FiShare
              size={25}
              style={{ marginRight: '10px' }}
            />{' '}
            <p>{t('addToHomescreen.iosInstallInstructions1')}</p>
          </div>
          <div className="d-flex">
            <p>
              <AiOutlinePlusSquare
                size={25}
                style={{ marginRight: '10px' }}
              />{' '}
              {t('addToHomescreen.iosInstallInstructions2')}
            </p>
          </div>
          <div className="d-flex">
            <p>
              <MdAddToHomeScreen
                size={25}
                style={{ marginRight: '10px' }}
              />{' '}
              {t('addToHomescreen.iosInstallInstructions3')}
            </p>
          </div>
        </div>
      ),
    });
  };

  const renderInstallButtonOrMessage = () => {
    if (isAppInstalled) return null;
    if (deferredPrompt || isMobileSafari() || !isCompatible) {
      if (type === 'link') {
        return (
          <StyledLink
            className="nav-link u-cursor-pointer"
            onClick={handleClick}
          >
            {icon}
            {t(label)}
          </StyledLink>
        );
      }
      if (type === 'button') {
        return (
          <Button
            btnType="secondary"
            onClick={handleClick}
          >
            {t(label)}
          </Button>
        );
      }
      if (type === 'footer-link') {
        return (
          <StyledFooterLink
            className="nav-link u-cursor-pointer"
            onClick={handleClick}
          >
            {icon}
            {t(label)}
          </StyledFooterLink>
        );
      }
      return (
        <div
          className="nav-link u-cursor-pointer"
          onClick={handleClick}
        >
          {icon}
          {t(label)}
        </div>
      );
    }
    return null;
  };

  return <div>{renderInstallButtonOrMessage()}</div>;
};

export default AddToHomeScreenComponent;

const StyledLink = styled.div`
  color: #716aca;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledFooterLink = styled.div`
  &:hover {
    color: #716aca;
  }
`;
